import React from 'react'
import { useTranslation } from 'react-i18next'
import { Card, CardContent, CardActions, Typography, Button, Chip, Box } from '@mui/material'
import {
  PlayArrow as PlayArrowIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  Warning as WarningIcon,
} from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'
import { Enrollment } from '../../types/enrollment'

interface EnrollmentCardProps {
  enrollment: Enrollment
}

export const EnrollmentCard: React.FC<EnrollmentCardProps> = ({ enrollment }) => {
  const { t, i18n } = useTranslation('enrollments')
  const navigate = useNavigate()

  const getStatusInfo = (status: string) => {
    switch (status) {
      case 'ASSIGNED':
        return {
          label: t('card.status.assigned'),
          color: 'default' as const,
          icon: <ScheduleIcon />,
        }
      case 'IN_PROGRESS':
        return {
          label: t('card.status.inProgress'),
          color: 'primary' as const,
          icon: <PlayArrowIcon />,
        }
      case 'COMPLETED':
        return {
          label: t('card.status.completed'),
          color: 'success' as const,
          icon: <CheckCircleIcon />,
        }
      case 'OVERDUE':
        return {
          label: t('card.status.overdue'),
          color: 'error' as const,
          icon: <WarningIcon />,
        }
      default:
        return {
          label: status,
          color: 'default' as const,
          icon: <ScheduleIcon />,
        }
    }
  }

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-'
    return new Date(dateString).toLocaleDateString(i18n.language === 'en' ? 'en-US' : 'ja-JP')
  }

  const handleStartTest = () => {
    navigate(`/test/${enrollment.test.id}?enrollmentId=${enrollment.id}`)
  }

  const statusInfo = getStatusInfo(enrollment.status)
  const isCompleted = enrollment.status === 'COMPLETED'
  const canStart = enrollment.status === 'ASSIGNED' || enrollment.status === 'IN_PROGRESS'

  return (
    <Card
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        minWidth: 0,
        boxSizing: 'border-box',
      }}
    >
      <CardContent
        sx={{
          flexGrow: 1,
          p: { xs: 1.5, sm: 2 },
          '&:last-child': { pb: { xs: 1.5, sm: 2 } },
        }}
      >
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="flex-start"
          gap={1}
          mb={{ xs: 1, sm: 2 }}
        >
          <Typography
            variant="h6"
            component="h3"
            gutterBottom
            sx={{
              flexGrow: 1,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              minWidth: 0,
              maxWidth: { xs: '200px', sm: 'none' },
              fontSize: { xs: '1rem', sm: '1.25rem' },
            }}
          >
            {enrollment.test.title}
          </Typography>
          <Chip
            icon={statusInfo.icon}
            label={statusInfo.label}
            color={statusInfo.color}
            size="small"
            sx={{ flexShrink: 0 }}
          />
        </Box>

        <Typography variant="body2" color="text.secondary" paragraph>
          {enrollment.test.description}
        </Typography>

        <Box mb={{ xs: 1, sm: 2 }}>
          <Chip
            label={enrollment.test.complianceRule.category}
            variant="outlined"
            size="small"
            sx={{
              mr: 1,
              mb: 1,
              fontSize: { xs: '0.6875rem', sm: '0.8125rem' },
              height: { xs: 24, sm: 32 },
            }}
          />
        </Box>

        <Box mb={{ xs: 1, sm: 2 }}>
          <Typography variant="caption" color="text.secondary" display="block">
            {t('card.passingScore', { score: enrollment.test.passingScore })}
          </Typography>
          {enrollment.test.timeLimit && (
            <Typography variant="caption" color="text.secondary" display="block">
              {t('card.timeLimit', { minutes: enrollment.test.timeLimit })}
            </Typography>
          )}
          <Typography variant="caption" color="text.secondary" display="block">
            {t('card.dueDate', { date: formatDate(enrollment.dueDate) })}
          </Typography>
        </Box>

        {/* 最新結果 */}
        {enrollment.latestResult && (
          <Box
            sx={{
              p: { xs: 1, sm: 2 },
              bgcolor: enrollment.latestResult.passed ? 'success.light' : 'error.light',
              borderRadius: 1,
              mb: { xs: 1, sm: 2 },
            }}
          >
            <Typography
              variant="body2"
              fontWeight="bold"
              sx={{
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
              }}
            >
              {t('card.latestResult', { score: enrollment.latestResult.score })} (
              {enrollment.latestResult.passed ? t('card.passed') : t('card.failed')})
            </Typography>
            <Typography variant="caption" display="block">
              {t('card.attemptCount', { count: enrollment.latestResult.attempt })}
            </Typography>
            <Typography variant="caption" display="block">
              {t('card.completedAt', { date: formatDate(enrollment.latestResult.completedAt) })}
            </Typography>
          </Box>
        )}

        {/* 期限警告 */}
        {enrollment.isOverdue && (
          <Box
            sx={{
              p: { xs: 0.5, sm: 1 },
              bgcolor: 'error.light',
              borderRadius: 1,
              mb: { xs: 1, sm: 2 },
            }}
          >
            <Typography variant="caption" color="error.dark" fontWeight="bold">
              {t('card.overdueLabel')}
            </Typography>
          </Box>
        )}
      </CardContent>

      <CardActions>
        {canStart && (
          <Button
            variant="contained"
            startIcon={<PlayArrowIcon />}
            onClick={handleStartTest}
            fullWidth
          >
            {enrollment.status === 'ASSIGNED' ? t('card.startTest') : t('card.continueTest')}
          </Button>
        )}

        {isCompleted && !enrollment.latestResult?.passed && (
          <Button
            variant="outlined"
            startIcon={<PlayArrowIcon />}
            onClick={handleStartTest}
            fullWidth
          >
            {t('card.retryTest')}
          </Button>
        )}
      </CardActions>
    </Card>
  )
}
